import { PartnerTierListType } from "../../../../../../../redux/slices/partner-program/Types";

/**
 * Get the maximum points value from tier list for Y-axis range
 * If highest tier has null max_points, find the highest available max_points
 */
export const getMaxPointsFromTierList = (tierList: PartnerTierListType): number => {
  if (!tierList || tierList.length === 0) return 10000;
  
  // Filter out null/undefined max_points and find the highest
  const validMaxPoints = tierList
    .map((tier) => tier.max_points)
    .filter((points): points is number => points !== null && points !== undefined);
  
  if (validMaxPoints.length === 0) return 10000;
  
  return Math.max(...validMaxPoints);
};

/**
 * Get tier names array from tier list for categorical Y-axis
 * Returns array sorted by min_points (lowest to highest tier)
 */
export const getTierNamesFromTierList = (tierList: PartnerTierListType): string[] => {
  if (!tierList || tierList.length === 0) return [];
  
  // Sort by min_points to get proper tier order (lowest to highest)
  const sortedTiers = [...tierList].sort((a, b) => a.min_points - b.min_points);
  return sortedTiers.map((tier) => tier.tier_name);
};

/**
 * Get tier name to numeric index mapping for chart plotting
 * Used to convert tier names to Y values for the chart
 */
export const getTierNameToIndexMap = (tierList: PartnerTierListType): Record<string, number> => {
  const tierNames = getTierNamesFromTierList(tierList);
  const map: Record<string, number> = {};
  tierNames.forEach((name, index) => {
    map[name] = index;
  });
  return map;
};

/**
 * Calculate Y-axis domain for numeric charts (licenses)
 * Returns [min, max] with some padding
 */
export const calculateYAxisDomain = (values: number[]): [number, number] => {
  if (!values || values.length === 0) return [0, 100];
  
  const min = Math.min(...values);
  const max = Math.max(...values);
  
  // Add 10% padding on both sides
  const padding = (max - min) * 0.1 || max * 0.1 || 10;
  
  return [
    Math.max(0, Math.floor(min - padding)),
    Math.ceil(max + padding),
  ];
};

/**
 * Format date label from API response (e.g., "2026-01" to "Jan 2026")
 */
export const formatDateLabel = (dateStr: string): string => {
  if (!dateStr) return "";
  
  // Handle full date format (YYYY-MM-DD)
  if (dateStr.includes("-") && dateStr.length > 7) {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  
  // Handle month format (YYYY-MM)
  if (dateStr.includes("-")) {
    const [year, month] = dateStr.split("-");
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  }
  
  return dateStr;
};

/**
 * Transform API data to chart-ready format
 */
export const transformTrendDataForChart = (
  labels: string[],
  values: (string | number)[]
): { label: string; value: string | number }[] => {
  return labels.map((label, index) => ({
    label: formatDateLabel(label),
    value: values[index],
  }));
};

/**
 * Transform tier status data for chart (convert tier names to numeric values)
 */
export const transformTierStatusDataForChart = (
  labels: string[],
  tierNames: string[],
  tierList: PartnerTierListType
): { label: string; value: number; tierName: string }[] => {
  const tierMap = getTierNameToIndexMap(tierList);
  
  return labels.map((label, index) => ({
    label: formatDateLabel(label),
    value: tierMap[tierNames[index]] ?? 0,
    tierName: tierNames[index] || "",
  }));
};

/**
 * Get the latest value and date from trend data
 */
export const getLatestTrendInfo = (
  labels: string[],
  values: (string | number)[]
): { latestValue: string | number | null; latestDate: string | null } => {
  if (!labels.length || !values.length) {
    return { latestValue: null, latestDate: null };
  }
  
  const lastIndex = labels.length - 1;
  return {
    latestValue: values[lastIndex],
    latestDate: formatDateLabel(labels[lastIndex]),
  };
};

/**
 * Calculate percentage change from first to last value
 */
export const calculateTrendChange = (
  values: number[]
): { change: number; percentage: number; isPositive: boolean; canCalculatePercentage: boolean } => {
  if (!values || values.length < 2) {
    return { change: 0, percentage: 0, isPositive: true, canCalculatePercentage: false };
  }
  
  const firstValue = values[0];
  const lastValue = values[values.length - 1];
  const change = lastValue - firstValue;
  
  // Can't calculate percentage if first value is 0
  const canCalculatePercentage = firstValue !== 0;
  const percentage = canCalculatePercentage ? (change / firstValue) * 100 : 0;
  
  return {
    change,
    percentage: Math.abs(percentage),
    isPositive: change >= 0,
    canCalculatePercentage,
  };
};

/**
 * Format number with commas for display
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString("en-US");
};

/**
 * Available time periods for the API
 */
export const TIME_PERIODS = [
  { label: "6 Month", value: "6month" },
  { label: "YTD", value: "ytd" },
  { label: "1 Year", value: "1year" },
  { label: "5 Year", value: "5year" },
  { label: "Max", value: "max" },
];
